/**
 * Profit/Loss Chart component using Recharts
 */

import { useMemo, useState, useEffect } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { calculateCumulativePL } from '@/utils/portfolioUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { IconChartLine } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import type { GoldAsset } from '@/types/asset'
import type { SaleTransaction } from '@/types/sale'
import type { GoldPriceHistory } from '@/types/price'

interface ProfitLossChartProps {
  assets: GoldAsset[]
  sales: SaleTransaction[]
  priceHistory: GoldPriceHistory[]
  startDate?: string
  endDate?: string
}

export function ProfitLossChart({
  assets,
  sales,
  priceHistory,
  startDate,
  endDate,
}: ProfitLossChartProps) {
  const chartData = useMemo(() => {
    let plData = calculateCumulativePL(assets, sales, priceHistory)

    // Filter by date range if provided
    if (startDate || endDate) {
      plData = plData.filter((item) => {
        const date = new Date(item.date)
        if (startDate && date < new Date(startDate)) return false
        if (endDate && date > new Date(endDate)) return false
        return true
      })
    }

    // Limit to 1000 data points for performance
    if (plData.length > 1000) {
      const step = Math.ceil(plData.length / 1000)
      plData = plData.filter((_, index) => index % step === 0)
    }

    return plData.map((item) => ({
      date: formatLocalDate(item.date, 'dd/MM/yyyy'),
      dateValue: item.date,
      profitLoss: item.profitLoss,
    }))
  }, [assets, sales, priceHistory, startDate, endDate])

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: Array<{
      value: number
      payload: { date: string; value: number }
    }>
  }) => {
    if (active && payload && payload.length && payload[0]) {
      const firstPayload = payload[0]
      const value = firstPayload.value
      const isPositive = value >= 0
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-foreground mb-2">{firstPayload.payload.date}</p>
          <p className={cn(
            "text-sm font-medium",
            isPositive ? "text-green-600" : "text-red-600"
          )}>
            Lợi nhuận/Lỗ: {formatVND(value)}
          </p>
        </div>
      )
    }
    return null
  }

  // Determine line color based on data
  const lineColor = useMemo(() => {
    if (chartData.length === 0) return '#10b981'
    const lastValue = chartData[chartData.length - 1]?.profitLoss ?? 0
    return lastValue >= 0 ? '#10b981' : '#ef4444'
  }, [chartData])

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biểu đồ Lợi nhuận/Lỗ</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconChartLine className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Không có dữ liệu để hiển thị</EmptyTitle>
              <EmptyDescription>
                Thêm tài sản và giao dịch để xem biểu đồ lợi nhuận/lỗ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ResponsiveContainer width="100%" height={300} className="sm:h-[400px]">
            <LineChart 
              data={chartData} 
              margin={{ 
                top: 5, 
                right: isMobile ? 10 : 30, 
                left: isMobile ? 0 : 20, 
                bottom: isMobile ? 60 : 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                angle={isMobile ? -90 : -45}
                textAnchor={isMobile ? "middle" : "end"}
                height={isMobile ? 100 : 80}
                interval="preserveStartEnd"
                className="text-muted-foreground"
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                tickFormatter={(value) => formatVND(value)}
                width={isMobile ? 60 : 100}
                className="text-muted-foreground"
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="profitLoss"
                stroke={lineColor}
                strokeWidth={2}
                name="Lợi nhuận/Lỗ"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

